// /src/components/WordCard.tsx
import React from 'react';
import { WordData } from '../types/game';

interface WordCardProps {
  data: WordData; 
  isDraggable: boolean; 
  isFaceUp: boolean; 
  stackIndex: number; 
  onDragStart: (word: string) => void; 
  onAddToVocab: (wordData: WordData) => void;
  onCardClick?: (word: string) => void; // 新增点击回调，用于移动端点击移动
  children?: React.ReactNode; // 支持嵌套渲染
  overlapOffset?: number; // 支持自定义偏移量
}

const WordCard: React.FC<WordCardProps> = ({ 
    data, 
    isDraggable, 
    isFaceUp, 
    stackIndex,
    onDragStart, 
    onAddToVocab,
    onCardClick,
    children,
    overlapOffset
}) => {

  // 专门处理加号点击
  const handlePlusClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止冒泡
    e.preventDefault(); 
    onAddToVocab(data); 
  };
  
  // 处理点击事件 (Tap to Move)
  const handleClick = (e: React.MouseEvent) => {
      // 只有在可拖拽（即正面朝上且符合规则）时才允许点击移动
      // 但在这里我们只做传递，具体规则由上层判断
      if (onCardClick && isDraggable) {
          e.stopPropagation();
          onCardClick(data.word);
      }
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (isDraggable) {
      e.stopPropagation(); // 关键：阻止事件冒泡，确保只拖动选中的卡片（及其子元素）
      e.dataTransfer.setData("text/plain", data.word);
      onDragStart(data.word);
    } else {
      e.preventDefault();
    }
  };

  // 如果提供了 overlapOffset，则使用它；否则使用 stackIndex 计算 absolute top
  const topValue = overlapOffset !== undefined ? `${overlapOffset}px` : `${stackIndex * 25}px`;

  const cardStyle: React.CSSProperties = {
      top: topValue,
      zIndex: stackIndex,
      cursor: isDraggable ? 'grab' : 'default' 
  };

  const isParent = data.type === 'PARENT';
  const parentStyle = isParent ? {
      border: '3px solid #FFD700', 
      backgroundColor: '#FFFBE6', 
      fontWeight: '900',
      boxShadow: '0 0 8px rgba(255, 215, 0, 0.8)'
  } : {};

  if (!isFaceUp) {
    return (
      <div 
        className="card-back" 
        style={cardStyle}
      >
        {children}
      </div>
    );
  }

  return (
    <div 
      className={`word-card ${isDraggable ? 'draggable' : ''}`}
      draggable={isDraggable}
      onDragStart={handleDragStart}
      onClick={handleClick}
      style={{...cardStyle, ...parentStyle}}
    >
      {isParent && <span style={{position: 'absolute', top: '2px', left: '2px', fontSize: '14px', zIndex: 5}}>👑</span>}
      
      {/* 显示优先级：图片 > Emoji > 单词 */}
      {typeof data.image === 'string' && data.image.trim() !== "" ? (
          <img 
            src={data.image} 
            alt={data.word} 
            style={{ 
                maxWidth: '80%', 
                maxHeight: '80%', 
                objectFit: 'contain',
                pointerEvents: 'none' // 防止图片被拖拽干扰卡片拖拽
            }} 
          />
      ) : data.emoji ? (
          <div style={{ fontSize: '3em', lineHeight: '1' }}>{data.emoji}</div>
      ) : (
          <span className="word-text">{data.word}</span>
      )}
      
      {/* 修复：事件绑定在 span 上，并防止冒泡和拖拽干扰 */}
      <span 
        className="vocab-hint" 
        onClick={handlePlusClick}
        onMouseDown={(e) => e.stopPropagation()} 
        style={{ 
            position: 'absolute',
            top: '5px',
            right: '5px',
            zIndex: 10, 
            cursor: 'pointer',
            fontSize: '16px',
            lineHeight: 1
        }} 
      >
        ➕
      </span>
      
      {children}
    </div>
  );
};

export default WordCard;
